import { useBookingFlow } from "../../context/use-booking-flow";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  CircleCheck,
  Home,
  FilePlus2,
  Link2Off,
  SquarePen,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export const BookingResult = () => {
  const { bookingFlowStatus, errorMessages, resetFlow, moveToSummaryStep } =
    useBookingFlow();
  const navigate = useNavigate();

  const renderStatus = () => {
    if (bookingFlowStatus.booking === "error") {
      return (
        <div className="flex flex-col justify-start items-center gap-4 inline-flex">
          <Link2Off className="h-16 w-16" />
          <div className="h-[73px] flex-col justify-start items-start gap-px inline-flex">
            <div className="self-stretch text-center text-[#08080a] text-2xl font-semibold font-['Inter'] leading-loose">
              Algo deu errado ao criar a reserva
            </div>
            <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Ocorreu um erro durante o processo.
              <br />
              Por favor, verifique sua conexão e tente novamente mais tarde.
            </div>
          </div>
          <p className="text-sm text-gray-500 my-2">{errorMessages.booking}</p>
        </div>
      );
    }

    if (
      bookingFlowStatus.booking === "success" &&
      bookingFlowStatus.upsell === "error"
    ) {
      return (
        <div className="flex flex-col justify-start items-center gap-4 inline-flex">
          <Link2Off className="h-16 w-16" />
          <div className="h-[73px] flex-col justify-start items-start gap-px inline-flex">
            <div className="self-stretch text-center text-[#08080a] text-2xl font-semibold font-['Inter'] leading-loose">
              Algo deu errado ao realizar upsell de pacotes
            </div>
            <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Ocorreu um erro durante o processo.
              <br />
              Por favor, verifique sua conexão e tente novamente mais tarde.
            </div>
          </div>
          <p className="text-sm text-gray-500 my-2">{errorMessages.upsell}</p>
        </div>
      );
    }

    if (
      bookingFlowStatus.poContract === "success" &&
      bookingFlowStatus.upsell === "error"
    ) {
      return (
        <div className="flex flex-col justify-start items-center gap-4 inline-flex">
          <Link2Off className="h-16 w-16" />
          <div className="h-[73px] flex-col justify-start items-start gap-px inline-flex">
            <div className="self-stretch text-center text-[#08080a] text-2xl font-semibold font-['Inter'] leading-loose">
              Algo deu errado ao realizar upsell de pacotes
            </div>
            <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight w-full">
              Ocorreu um erro durante o processo.
              <br />
              Por favor, verifique sua conexão e tente novamente mais tarde.
            </div>
          </div>
          <Card className="bg-slate-100 text-black max-w-md p-4 my-4 rounded-md">
            <p className="text-sm text-red-500 my-2">{errorMessages.upsell}</p>
          </Card>
        </div>
      );
    }

    if (bookingFlowStatus.poContract === "error") {
      return (
        <div className="flex flex-col justify-start items-center gap-4 inline-flex">
          <Link2Off className="h-16 w-16" />
          <div className="h-[73px] flex-col justify-start items-start gap-px inline-flex">
            <div className="self-stretch text-center text-[#08080a] text-2xl font-semibold font-['Inter'] leading-loose">
              Algo deu errado ao criar o contrato
            </div>
            <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Ocorreu um erro durante o processo.
              <br />
              Por favor, verifique sua conexão e tente novamente mais tarde.
            </div>
          </div>
          {errorMessages.poContract && (
            <Card className="bg-slate-100 text-black max-w-md p-4 my-4 rounded-md">
              <p className="text-sm text-red-500 my-2">
                {errorMessages.poContract}
              </p>
            </Card>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col justify-start items-center gap-4 inline-flex">
        <CircleCheck className="h-16 w-16" />
        <div className="flex-col justify-start items-center gap-px inline-flex">
          <div className="text-center text-[#08080a] text-2xl font-semibold font-['Inter'] leading-loose">
            Sucesso!
          </div>
          <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight mb-4">
            {`${bookingFlowStatus.poContract === "success" ? "Seu contrato foi criado" : "Sua reserva de escritório privativo foi criada"} com sucesso!`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px]">
      {renderStatus()}
      <div className="flex gap-4">
        {(bookingFlowStatus.booking === "error" ||
          bookingFlowStatus.upsell === "error" ||
          bookingFlowStatus.poContract === "error") && (
          <Button
            className="justify-center items-center gap-2 inline-flex hover:bg-white group border border-gray-200"
            onClick={moveToSummaryStep}
          >
            <SquarePen className="h-4 w-4 group-hover:text-black transition-colors duration-200" />
            <label className="group-hover:text-black transition-colors duration-200">
              Editar reserva
            </label>
          </Button>
        )}
        <Button
          className="bg-white text-black border border-[#e3e3e7] justify-center items-center gap-2 inline-flex hover:bg-gray-900 group"
          onClick={() =>
            navigate(
              `/private-office/${
                bookingFlowStatus.poContract === "success" ||
                bookingFlowStatus.poContract === "error"
                  ? "contracts/list"
                  : "bookings/list"
              }`,
            )
          }
        >
          <Home className="h-4 w-4 group-hover:text-white transition-colors duration-200" />
          <label className="group-hover:text-white transition-colors duration-200">
            Retornar ao início
          </label>
        </Button>
        {(bookingFlowStatus.booking === "success" ||
          bookingFlowStatus.poContract === "success") &&
          bookingFlowStatus.upsell !== "error" && (
            <Button
              className="justify-center items-center gap-2 inline-flex hover:bg-white group border border-gray-200"
              onClick={resetFlow}
            >
              <FilePlus2 className="h-4 w-4 group-hover:text-black transition-colors duration-200" />
              <label className="group-hover:text-black transition-colors duration-200">
                {bookingFlowStatus.poContract === "success"
                  ? "Retornar ao contrato"
                  : `Criar outra reserva`}
              </label>
            </Button>
          )}
      </div>
    </div>
  );
};
